import type { ReactElement } from "react";
import { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import useLatestValue from "app/custom-hooks/useLatestValue";
import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import { DEFAULT_NO_DATA_VIEW } from "app/pages/sales-intelligence/common-components/search-results-table/components/SearchResultsTable/const";
import managementTrackingService from "app/pages/sales-intelligence/modules/management/services/tracking/managementTrackingService";
import { resolveSignalsApiCustomAttributes } from "app/pages/sales-intelligence/modules/management/services/tracking/utils";
import { managementSignalsApiQueryKeys } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/key-factory/queryKeyFactory";
import { useSignalsApiStatusPollingQuery } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/queries/useSignalsApiStatusPollingQuery";
import validateIsSubscriptionComplete from "app/pages/sales-intelligence/modules/signals-api-webhook/components/WebhookTableForm/utils/validate";
import { MAX_WEBHOOK_SUBSCRIPTIONS_COUNT } from "app/pages/sales-intelligence/modules/signals-api-webhook/constants/limits";
import createEmptySubscription from "app/pages/sales-intelligence/modules/signals-api-webhook/factories/createEmptySubscription";
import useCreateColumnConfig from "app/pages/sales-intelligence/modules/signals-api-webhook/hooks/useCreateColumnConfig";
import { useRemoveWebhookSubscriptionMutation } from "app/pages/sales-intelligence/modules/signals-api-webhook/query/mutations/useRemoveWebhookSubscriptionMutation";
import { useSaveWebhookSubscriptionMutation } from "app/pages/sales-intelligence/modules/signals-api-webhook/query/mutations/useSaveWebhookSubscriptionMutation";
import subscriptionsValueTransformer from "app/pages/sales-intelligence/modules/signals-api-webhook/utils/subscriptionsValueTransformer";
import { subscriptionsWithIndex } from "app/pages/sales-intelligence/modules/signals-api-webhook/utils/subscriptionsWithIndex";
import type SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";

import type {
    ISignalsWebhookSubscription,
    IWebhookSubscriptionBaseModel,
    IWebhookSubscriptionTableModel,
} from "../../types/row-data";

import { StyledWebhookAddButtonWrapper, StyledWebhookTableFormWrapper } from "./styles";

interface IProps {
    subscriptions: readonly ISignalsWebhookSubscription[];
    onTest: (subscription: IWebhookSubscriptionBaseModel) => void;
}

const WebhookTableForm = ({ subscriptions, onTest }: IProps): ReactElement => {
    const scrollContainerElement: HTMLElement | null = document.querySelector(".sw-layout-scrollable-element");
    const queryClient = useQueryClient();

    const { data } = useSignalsApiStatusPollingQuery();
    const { mutateAsync: saveSubscription } = useSaveWebhookSubscriptionMutation();
    const { mutateAsync: removeSubscription } = useRemoveWebhookSubscriptionMutation();

    const [subscriptionsValue, setSubscriptionsValue] = useState<IWebhookSubscriptionTableModel[]>(() =>
        subscriptionsValueTransformer(subscriptions),
    );

    const subscriptionsValueRef = useLatestValue(subscriptionsValue);

    useEffect(() => {
        setSubscriptionsValue(subscriptionsValueTransformer(subscriptions));
    }, [subscriptions]);

    const handleSave = ({ key, errors, ...subscription }: IWebhookSubscriptionTableModel, index: number): void => {
        if (!validateIsSubscriptionComplete(subscription)) {
            if (!subscription.callbackUrl && subscription.id) {
                removeSubscription(subscription.id);
            }

            return;
        }

        const isNewSubscription = !subscription.id;

        saveSubscription(subscription).then((savedSubscription: ISignalsWebhookSubscription) => {
            queryClient.invalidateQueries({ queryKey: managementSignalsApiQueryKeys.getStatus() });

            managementTrackingService.trackSignalApiEvent(
                "insert",
                `add webhook/${decodeURI(savedSubscription.callbackUrl)}`,
                resolveSignalsApiCustomAttributes(data),
            );

            setSubscriptionsValue((prev) =>
                subscriptionsWithIndex(
                    prev.map((row, i) => {
                        if (isNewSubscription ? i === index : row.id === savedSubscription.id) {
                            return { key, ...savedSubscription };
                        }

                        return row;
                    }),
                ),
            );
        });
    };

    const handleChange = (
        rowIndex: number,
        field: keyof IWebhookSubscriptionTableModel,
        value: IWebhookSubscriptionTableModel[keyof IWebhookSubscriptionTableModel],
    ) => {
        const row = subscriptionsValueRef.current[rowIndex];

        if (field === "signalsPerHook") {
            managementTrackingService.trackSignalApiClickEvent(
                `select batch/${value}`,
                resolveSignalsApiCustomAttributes(data),
            );
        }

        if (field === "signalTypes") {
            managementTrackingService.trackSignalApiClickEvent(
                `select signal type/${(value as SignalType[]).join(",")}`,
                resolveSignalsApiCustomAttributes(data),
            );
        }

        const newRowValue = { ...row, [field]: value };

        setSubscriptionsValue((prev) => {
            const updated = [...prev];
            updated[rowIndex] = newRowValue;
            return updated;
        });

        handleSave(newRowValue, rowIndex);
    };

    const handleDelete = ({ id, index }: IWebhookSubscriptionTableModel): void => {
        if (!id) {
            setSubscriptionsValue((prevValue) => {
                return subscriptionsValueTransformer(prevValue.filter((_, i: number) => i !== index));
            });

            return;
        }

        removeSubscription(id).then(() => {
            queryClient.invalidateQueries({ queryKey: managementSignalsApiQueryKeys.getStatus() });

            managementTrackingService.trackSignalApiClickEvent(
                `remove webhook`,
                resolveSignalsApiCustomAttributes(data),
            );

            setSubscriptionsValue((prevValue) => {
                return subscriptionsValueTransformer(prevValue.filter((subscription) => id !== subscription.id));
            });
        });
    };

    const handleAdd = () => {
        managementTrackingService.trackSignalApiClickEvent(
            `add another webhook`,
            resolveSignalsApiCustomAttributes(data),
        );
        setSubscriptionsValue((prev) => subscriptionsWithIndex([...prev, createEmptySubscription()]));
    };

    const handleError = useCallback(
        (error: string | null, index: number, fieldName: keyof IWebhookSubscriptionBaseModel) => {
            setSubscriptionsValue((prev) => {
                return prev.map((row, i) => {
                    if (i !== index) {
                        return row;
                    }

                    const errors = row.errors || {};

                    if (!error) {
                        delete errors[fieldName];
                    } else {
                        errors[fieldName] = error;
                    }

                    return {
                        ...row,
                        errors: objectKeys(errors).length ? errors : undefined,
                    };
                });
            });
        },
        [],
    );

    const columns: ColumnsType<IWebhookSubscriptionTableModel> = useCreateColumnConfig(
        subscriptionsValue,
        handleChange,
        onTest,
        handleDelete,
        handleError,
    );

    const addButton =
        subscriptionsValue.length < MAX_WEBHOOK_SUBSCRIPTIONS_COUNT ? (
            <StyledWebhookAddButtonWrapper>
                <IconButton
                    type="flat"
                    iconName="add"
                    onClick={handleAdd}
                    dataAutomation="add-another-endpoint"
                >
                    {translate("si.management.integration.signals-api.section.webhook.add-button")}
                </IconButton>
            </StyledWebhookAddButtonWrapper>
        ) : null;

    // todo: remove `key={subscriptionsValue.length === 1 ? 1 : 0}` after fix SI-6764
    return (
        <StyledWebhookTableFormWrapper>
            <Table
                key={subscriptionsValue.length === 1 ? 1 : 0}
                style={{
                    overflow: "visible",
                }}
                sticky={scrollContainerElement ? { getContainer: () => scrollContainerElement } : undefined}
                isLoading={false}
                data={subscriptionsValue}
                columnConfig={columns}
                withArrowButtons={false}
                noData={DEFAULT_NO_DATA_VIEW}
            />

            {addButton}
        </StyledWebhookTableFormWrapper>
    );
};

export default WebhookTableForm;
